import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Search, Download, Copy, Check, Users, Building2, Calendar, UserCheck, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Screen Component
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.authenticated) {
        sessionStorage.setItem("truvio_admin_auth", "true");
        onLogin();
      } else {
        setError("Incorrect password");
      }
    } catch (err) {
      setError("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-truvio-bg flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="font-playfair text-2xl font-black text-truvio-text inline-block">
            Truv<span className="italic text-truvio-primary">io</span>
          </Link>
          <p className="text-truvio-muted text-sm mt-2">Admin Dashboard</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-truvio-surface border border-truvio-border p-8">
          <label className="block text-truvio-muted text-xs uppercase tracking-wider mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(""); }}
            placeholder="Enter admin password"
            className="w-full bg-truvio-bg border border-truvio-border px-4 py-3 text-truvio-text placeholder-truvio-muted/50 focus:outline-none focus:border-truvio-primary transition-colors"
            autoFocus
            data-testid="admin-password"
          />
          {error && (
            <p className="text-red-400 text-sm mt-2" data-testid="login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary mt-4"
            data-testid="admin-login-btn"
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-truvio-surface border border-truvio-border p-6">
    <div className="flex items-center gap-4">
      <div 
        className="w-12 h-12 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <p className="text-truvio-muted text-xs uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-bold text-truvio-text mt-1">{value}</p>
      </div>
    </div>
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    new: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    contacted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    converted: "bg-green-500/20 text-green-400 border-green-500/30"
  };
  
  return (
    <span className={`px-2 py-1 text-xs rounded border ${styles[status] || styles.new}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Role Badge Component
const RoleBadge = ({ role }) => {
  if (role === "reviewer") {
    return (
      <span className="px-2 py-1 text-xs rounded bg-truvio-primary/20 text-truvio-primary border border-truvio-primary/30">
        Reviewer
      </span>
    );
  }
  return (
    <span className="px-2 py-1 text-xs rounded bg-truvio-accent/20 text-truvio-accent border border-truvio-accent/30">
      Business
    </span>
  );
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-xs text-truvio-muted hover:text-truvio-text transition-colors px-2 py-1 rounded hover:bg-truvio-border/50"
    >
      {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

// Delete Button Component with inline confirmation
const DeleteButton = ({ entryId, onDelete }) => {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/waitlist/${entryId}`);
      onDelete(entryId);
    } catch (error) {
      console.error("Failed to delete:", error);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-truvio-muted">Delete?</span>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
        >
          {deleting ? "..." : "Yes"}
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="text-xs text-truvio-muted hover:text-truvio-text px-2 py-1 rounded hover:bg-truvio-border/50 transition-colors"
        >
          No
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="flex items-center gap-1 text-xs text-truvio-muted hover:text-red-400 transition-colors px-2 py-1 rounded hover:bg-red-500/10"
      title="Delete"
    >
      <i className="ph-bold ph-trash text-sm"></i>
    </button>
  );
};

// Status Dropdown Component
const StatusDropdown = ({ entryId, currentStatus, onStatusChange }) => {
  const [updating, setUpdating] = useState(false);

  const handleChange = async (e) => {
    const newStatus = e.target.value;
    setUpdating(true);
    try {
      await axios.patch(`${API}/waitlist/${entryId}/status`, { status: newStatus });
      onStatusChange(entryId, newStatus);
    } catch (error) {
      console.error("Failed to update status:", error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <select
      value={currentStatus}
      onChange={handleChange}
      disabled={updating}
      className="bg-truvio-bg border border-truvio-border text-truvio-text text-xs px-2 py-1 rounded focus:outline-none focus:border-truvio-primary cursor-pointer"
    >
      <option value="new">New</option>
      <option value="contacted">Contacted</option>
      <option value="converted">Converted</option>
    </select>
  );
};

// Format date helper
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${month} ${year}, ${time}`;
};

// Main Dashboard Component
const Dashboard = ({ onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, reviewers: 0, businesses: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/waitlist`),
        axios.get(`${API}/waitlist/stats`)
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (entryId, newStatus) => {
    setEntries(entries.map(e => 
      e.id === entryId ? { ...e, status: newStatus } : e
    ));
  };

  const handleDelete = (entryId) => {
    const deletedEntry = entries.find(e => e.id === entryId);
    if (deletedEntry) {
      // Update entries
      setEntries(entries.filter(e => e.id !== entryId));
      // Update stats
      setStats(prev => ({
        ...prev,
        total: prev.total - 1,
        reviewers: deletedEntry.role === 'reviewer' ? prev.reviewers - 1 : prev.reviewers,
        businesses: deletedEntry.role === 'business' ? prev.businesses - 1 : prev.businesses,
        today: prev.today - 1 // Simplified - assumes deleted entry was from today
      }));
    }
  };

  // Filter and search entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // Role filter
      if (filterRole !== "all" && entry.role !== filterRole) return false;
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesEmail = entry.email.toLowerCase().includes(query);
        const matchesCompany = entry.company_name?.toLowerCase().includes(query);
        if (!matchesEmail && !matchesCompany) return false;
      }
      
      return true;
    });
  }, [entries, filterRole, searchQuery]);

  // Export to CSV
  const exportCSV = () => {
    const headers = ["email", "role", "company_name", "status", "created_at"];
    const rows = filteredEntries.map(entry => [
      entry.email,
      entry.role,
      entry.company_name || "",
      entry.status || "new",
      entry.created_at
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `truvio-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-truvio-bg">
      {/* Header */}
      <header className="border-b border-truvio-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl font-black text-truvio-text">
            Truv<span className="italic text-truvio-primary">io</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-truvio-muted text-sm">Admin Dashboard</span>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-truvio-muted hover:text-truvio-text transition-colors text-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard icon={Users} label="Total Signups" value={stats.total} color="#1a7a4a" />
          <StatsCard icon={UserCheck} label="Reviewers" value={stats.reviewers} color="#1a7a4a" />
          <StatsCard icon={Building2} label="Businesses" value={stats.businesses} color="#c8f064" />
          <StatsCard icon={Calendar} label="Today" value={stats.today} color="#6a6458" />
        </div>

        {/* Table Controls */}
        <div className="bg-truvio-surface border border-truvio-border p-4 mb-0 border-b-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 text-truvio-muted absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search email or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-truvio-bg border border-truvio-border pl-10 pr-4 py-2 text-sm text-truvio-text placeholder-truvio-muted/50 focus:outline-none focus:border-truvio-primary w-64"
                  data-testid="search-input"
                />
              </div>
              
              {/* Filter Tabs */}
              <div className="flex border border-truvio-border rounded overflow-hidden">
                {[
                  { key: "all", label: "All" },
                  { key: "reviewer", label: "Reviewers" },
                  { key: "business", label: "Businesses" }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFilterRole(tab.key)}
                    className={`px-4 py-2 text-xs transition-colors ${
                      filterRole === tab.key 
                        ? "bg-truvio-primary text-white" 
                        : "bg-truvio-bg text-truvio-muted hover:text-truvio-text"
                    }`}
                    data-testid={`filter-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Export Button */}
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 text-sm text-truvio-muted hover:text-truvio-text transition-colors px-4 py-2 border border-truvio-border hover:border-truvio-text/30"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-truvio-surface border border-truvio-border overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-truvio-muted">Loading...</div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <p className="text-truvio-muted">
                {entries.length === 0 
                  ? "No signups yet. Share the link and check back soon."
                  : "No results matching your search."
                }
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-truvio-border">
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-truvio-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries.map((entry, idx) => (
                  <tr 
                    key={entry.id} 
                    className={`border-b border-truvio-border/50 hover:bg-truvio-border/20 transition-colors ${
                      idx % 2 === 0 ? "bg-truvio-bg/30" : ""
                    }`}
                    data-testid={`waitlist-row-${idx}`}
                  >
                    <td className="px-4 py-3 text-sm text-truvio-muted">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-truvio-text">{entry.email}</td>
                    <td className="px-4 py-3"><RoleBadge role={entry.role} /></td>
                    <td className="px-4 py-3 text-sm text-truvio-muted">{entry.company_name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-truvio-muted">{formatDate(entry.created_at)}</td>
                    <td className="px-4 py-3">
                      <StatusDropdown 
                        entryId={entry.id} 
                        currentStatus={entry.status || "new"} 
                        onStatusChange={handleStatusChange}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <CopyButton text={entry.email} />
                        <DeleteButton entryId={entry.id} onDelete={handleDelete} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer count */}
        {filteredEntries.length > 0 && (
          <div className="text-truvio-muted text-xs mt-4">
            Showing {filteredEntries.length} of {entries.length} entries
          </div>
        )}
      </main>
    </div>
  );
};

// Main Admin Page Component
function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    document.title = "Truvio — Admin Dashboard";
    // Check if already authenticated
    const auth = sessionStorage.getItem("truvio_admin_auth");
    if (auth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("truvio_admin_auth");
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return <Dashboard onLogout={handleLogout} />;
}

export default AdminPage;
